// The follow-camera. A tilted perspective camera that trails the player from
// behind; Q/E orbit it around the player via `game.camYaw` (advanced in the
// loop). `cameraBasis` gives the loop the camera-relative forward/right vectors
// so "up" on the keyboard is always away from the camera.

import * as THREE from "three";
import { FOV, CAM_OFFSET, CAM_FAR } from "./config.ts";
import { WATER_LEVEL } from "./terrain.ts";
import { indoorWeight, INDOOR_ZOOM } from "./cutaway.ts";

const UP = new THREE.Vector3(0, 1, 0);
const RIGHT = new THREE.Vector3(1, 0, 0);

export function makeCamera(): THREE.PerspectiveCamera {
  return new THREE.PerspectiveCamera(FOV, 1, 0.1, CAM_FAR);
}

// Dev-only follow-camera tuning (the F7 panel, src/cameradev.ts). `zoom` dollies
// the camera toward/away from the player; `pitch` tilts the offset up/down around
// the player (a vertical orbit). The SHIPPED DEFAULT lives once in CAM_DEV_DEFAULTS
// below — both the initial state and resetCamDev() derive from it, so retuning the
// default is a one-line change and every "return to the default view" follows. Purely
// client-visual — no wire message, no server involvement (like the F6 toon panel).
export const CAM_DEV_DEFAULTS = { zoom: 0.5, pitch: 0.1 } as const;
export const CAM_DEV = { ...CAM_DEV_DEFAULTS };

/** Reset the dev camera tuning to the shipped default (F7 panel's reset). */
export function resetCamDev(): void {
  CAM_DEV.zoom = CAM_DEV_DEFAULTS.zoom;
  CAM_DEV.pitch = CAM_DEV_DEFAULTS.pitch;
}

/** THE gameplay follow-pose offset: the base CAM_OFFSET with the dev pitch/zoom
 *  tuning applied, rotated horizontally by camYaw. This is the single definition
 *  of "where the follow camera sits" — updateCamera positions from it each frame,
 *  and every cutscene EXIT eases back to it — so retuning zoom/pitch (or its
 *  default, CAM_DEV_DEFAULTS) moves the live view AND every "return to camera"
 *  together, instead of the exit gliding out to the raw un-tuned offset.
 *  Tilt around RIGHT (in the offset's canonical frame, before the yaw), scale for
 *  the dolly, then rotate horizontally by camYaw. Scale commutes with the
 *  rotations, so the order of multiplyScalar vs the applyAxisAngle calls is
 *  immaterial. */
export function followOffset(camYaw: number): THREE.Vector3 {
  // Indoor dolly multiplies on top of F7 CAM_DEV.zoom so retuning either stays
  // independent. indoorWeight() is one frame behind cutaway (camera runs first)
  // — the ease makes that lag invisible.
  const zoom = CAM_DEV.zoom * (1 + (INDOOR_ZOOM - 1) * indoorWeight());
  return CAM_OFFSET.clone()
    .applyAxisAngle(RIGHT, CAM_DEV.pitch)
    .multiplyScalar(zoom)
    .applyAxisAngle(UP, camYaw);
}

/** Power-shot full-charge FOV pull (× of base FOV at frac 1). */
export const CHARGE_FOV_MUL = 0.88;
/** Ease rate for FOV restore/pull (higher = snappier). */
const CHARGE_FOV_EASE = 10;

// Position the camera behind+above the target, rotated by camYaw, looking at it.
// Optional FOV pull while charging a power shot: pass chargeFrac 0→1 from combat
// (0 = no charge; 1 = fully charged). Camera stays presentation-dumb — no game imports.
export function updateCamera(
  camera: THREE.PerspectiveCamera,
  target: THREE.Object3D,
  camYaw: number,
  dt = 1 / 60,
  chargeFrac = 0,
) {
  camera.position.copy(target.position).add(followOffset(camYaw));
  camera.lookAt(target.position.x, target.position.y + 1.2, target.position.z);
  // Lerp FOV from normal → pull over the whole charge (not only at 100%).
  const t = Math.max(0, Math.min(1, chargeFrac));
  const want = FOV * (1 + (CHARGE_FOV_MUL - 1) * t);
  camera.fov += (want - camera.fov) * Math.min(1, dt * CHARGE_FOV_EASE);
  camera.updateProjectionMatrix();
}

// ---- Talk cutscene camera (side-on two-shot) ------------------------------
// During a conversation the camera swings to the side of the player→NPC line so
// both subjects are framed in profile, facing each other. Position + look target
// are eased toward the desired framing each frame for a smooth zoom-in; the side
// (left/right of the line) is locked at the start to whichever side the gameplay
// camera was already on, so it doesn't sweep through the subjects.
const _mid = new THREE.Vector3();
const _perp = new THREE.Vector3();
const _desiredPos = new THREE.Vector3();
const _desiredTarget = new THREE.Vector3();
const _camPos = new THREE.Vector3();
const _camTarget = new THREE.Vector3();
const _off = new THREE.Vector3();
const _lastPlayerPos = new THREE.Vector3(); // last talk-camera player pos (for exit yaw reconcile)
let talkSeeded = false;
let talkSide = 1;
let talkYaw = 0; // Q/E orbit offset around the two-shot midpoint (reset per conversation)

/** THE cutscene-camera ease step: lerp the persistent _camPos/_camTarget toward
 *  the current _desiredPos/_desiredTarget and apply. Every cutscene camera
 *  (talk / journal / fishing / their exits) funnels through this one copy, so
 *  the ease feel can't drift between activities. */
function easeApply(camera: THREE.PerspectiveCamera, dt: number): void {
  const k = Math.min(1, dt * 4); // ~0.25s ease
  _camPos.lerp(_desiredPos, k);
  _camTarget.lerp(_desiredTarget, k);
  camera.position.copy(_camPos);
  camera.lookAt(_camTarget);
}

/** Re-seed the eased talk camera so the next conversation starts easing from the
 *  live gameplay view (and re-picks the framing side + zeroes the Q/E orbit). Call
 *  when a talk begins. */
export function resetTalkCamera(): void {
  talkSeeded = false;
  talkYaw = 0;
}

export function updateTalkCamera(
  camera: THREE.PerspectiveCamera,
  playerPos: THREE.Vector3,
  npcPos: THREE.Vector3,
  dt: number,
  orbit = 0, // Q/E this frame (radians): orbit the two-shot around its midpoint
): void {
  talkYaw += orbit;
  _lastPlayerPos.copy(playerPos); // for talkFollowYaw() when the conversation ends
  _mid.set((playerPos.x + npcPos.x) / 2, (playerPos.y + npcPos.y) / 2, (playerPos.z + npcPos.z) / 2);
  // Direction player→NPC on the ground plane, and the left/right perpendicular.
  let dx = npcPos.x - playerPos.x, dz = npcPos.z - playerPos.z;
  const sep = Math.hypot(dx, dz) || 1e-6;
  dx /= sep; dz /= sep;
  _perp.set(-dz, 0, dx); // unit, perpendicular to the line, in the ground plane

  // On the first frame, pick the side the gameplay camera was already on (so the
  // swing is short) and seed the eased state from the current view.
  if (!talkSeeded) {
    talkSide = (camera.position.x - _mid.x) * _perp.x + (camera.position.z - _mid.z) * _perp.z >= 0 ? 1 : -1;
    _camPos.copy(camera.position);
    _camTarget.set(playerPos.x, playerPos.y + 1.2, playerPos.z); // ~gameplay lookAt
    talkSeeded = true;
  }

  const sideDist = sep * 1.1 + 4; // back off as the two stand farther apart
  const height = 3;
  // Side offset, orbited around the vertical axis through the midpoint by the Q/E
  // yaw (horizontal only, so height stays constant and never dips underground).
  _off.copy(_perp).multiplyScalar(talkSide * sideDist).applyAxisAngle(UP, talkYaw);
  _desiredPos.copy(_mid).add(_off);
  _desiredPos.y = _mid.y + height;
  _desiredTarget.set(_mid.x, _mid.y + 1.4, _mid.z);
  easeApply(camera, dt);
}

/** The follow-camera yaw that reproduces the two-shot's FINAL heading around the
 *  player (last talk-camera frame). Ending a talk seeds game.camYaw with this so
 *  the exit ease continues from where any Q/E orbit left the view instead of
 *  swinging the whole orbit back to a fixed "behind player" — talk.ts endTalk. */
export function talkFollowYaw(fallback: number): number {
  const dx = _camPos.x - _lastPlayerPos.x, dz = _camPos.z - _lastPlayerPos.z;
  if (dx * dx + dz * dz < 1e-6) return fallback; // never ran / degenerate — keep the current yaw
  return Math.atan2(dx, dz) - Math.atan2(CAM_OFFSET.x, CAM_OFFSET.z);
}

/** Ease the camera from wherever the talk two-shot left it back to the normal
 *  follow pose (behind+above `target`, rotated by camYaw). Reuses the same eased
 *  state as updateTalkCamera, so leaving a conversation glides back instead of
 *  cutting. game.ts runs this for a short window after a talk ends. */
export function updateTalkExitCamera(
  camera: THREE.PerspectiveCamera,
  target: THREE.Object3D,
  camYaw: number,
  dt: number,
): void {
  // Ease back to the actual tuned follow pose (followOffset), not the raw
  // CAM_OFFSET — otherwise leaving an activity glides out to full zoom / no pitch
  // and then updateCamera snaps back to the tuned view.
  _desiredPos.copy(followOffset(camYaw)).add(target.position);
  _desiredTarget.set(target.position.x, target.position.y + 1.2, target.position.z);
  easeApply(camera, dt);
}

// ---- First-person angling camera (plan 10) ---------------------------------
// While a line is out the camera eases to the angler's eye level, looking out
// over the water at the cast point — a true first-person shot with just the rod
// in front of you and the bobber centered, the defining view of Angling. Clones
// the talk-cutscene easing over the SAME _camPos/_camTarget (fishing locks input,
// so it never runs alongside a talk or the journal); game.ts branches on
// game.fishing / game.fishingExit.
let fishingSeeded = false;

/** Re-seed so a fresh cast eases in from the live gameplay view. */
export function resetFishingCamera(): void {
  fishingSeeded = false;
}

export function updateFishingCamera(
  camera: THREE.PerspectiveCamera,
  playerPos: THREE.Vector3,
  cast: { x: number; z: number },
  dt: number,
  waterY = WATER_LEVEL, // the surface height to aim at (studio arena passes its pond's y)
): void {
  let dx = cast.x - playerPos.x, dz = cast.z - playerPos.z;
  const d = Math.hypot(dx, dz) || 1e-6;
  dx /= d; dz /= d;
  if (!fishingSeeded) {
    _camPos.copy(camera.position);
    _camTarget.set(playerPos.x, playerPos.y + 1.2, playerPos.z);
    fishingSeeded = true;
  }
  // True first person: at eye level, nudged a hair FORWARD of the head along the
  // cast line so the body sits behind the lens (no back-of-head in frame) and only
  // the rod — held in the hand ahead of the chest — stays visible, aimed at the float.
  _desiredPos.set(playerPos.x + dx * 0.25, playerPos.y + 1.72, playerPos.z + dz * 0.25);
  _desiredTarget.set(cast.x, waterY + 0.4, cast.z);
  easeApply(camera, dt);
}

/** Ease back to the follow pose after the line resolves (mirrors talk exit). */
export function updateFishingExitCamera(
  camera: THREE.PerspectiveCamera,
  target: THREE.Object3D,
  camYaw: number,
  dt: number,
): void {
  updateTalkExitCamera(camera, target, camYaw, dt);
}

// ---- Over-the-shoulder aim camera (plan 11) ---------------------------------
// While the hunting bow is drawn the camera eases to just behind the archer's
// right shoulder, sighted down the aim line at the prey — the defining view of
// Stalking (the load-bearing "hunter, not archer" framing). Clones the
// talk-cutscene easing over the SAME _camPos/_camTarget (drawing locks input,
// so it never runs alongside a talk/journal/cast); game.ts branches on
// game.aiming / game.aimingExit.
let aimSeeded = false;

/** Re-seed so a fresh draw eases in from the live gameplay view. */
export function resetAimCamera(): void {
  aimSeeded = false;
}

export function updateAimCamera(
  camera: THREE.PerspectiveCamera,
  playerPos: THREE.Vector3,
  target: { x: number; y: number; z: number }, // prey mid-height (caller resolves feetY + ~0.6)
  dt: number,
): void {
  let dx = target.x - playerPos.x, dz = target.z - playerPos.z;
  const d = Math.hypot(dx, dz) || 1e-6;
  dx /= d; dz /= d;
  if (!aimSeeded) {
    _camPos.copy(camera.position);
    _camTarget.set(playerPos.x, playerPos.y + 1.2, playerPos.z);
    aimSeeded = true;
  }
  // Right-shoulder offset: back along the aim line, nudged perpendicular so the
  // archer frames the left third and the prey sits center-right of the sight.
  const px = -dz, pz = dx; // right perpendicular of the aim line
  _desiredPos.set(
    playerPos.x - dx * 1.7 + px * 0.6,
    playerPos.y + 1.85,
    playerPos.z - dz * 1.7 + pz * 0.6,
  );
  _desiredTarget.set(target.x, target.y, target.z);
  easeApply(camera, dt);
}

/** Ease back to the follow pose after the shot resolves / the draw cancels. */
export function updateAimExitCamera(
  camera: THREE.PerspectiveCamera,
  target: THREE.Object3D,
  camYaw: number,
  dt: number,
): void {
  updateTalkExitCamera(camera, target, camYaw, dt);
}

// ---- First-person cooking camera (plan 18) ---------------------------------
// While a cook is on the fire the camera eases to the cook's eye level looking
// DOWN over the campfire set a step ahead — a first-person shot with the fire
// and the roasting dish centered, so timing the pull is a held gaze on the pan
// (the defining view of Cooking). Clones the talk-cutscene easing over the SAME
// _camPos/_camTarget (cooking locks input, so it never runs alongside a talk/
// journal/cast/draw); game.ts branches on game.cooking / game.cookingExit.
let cookSeeded = false;

/** Re-seed so a fresh cook eases in from the live gameplay view. */
export function resetCookCamera(): void {
  cookSeeded = false;
}

export function updateCookCamera(
  camera: THREE.PerspectiveCamera,
  playerPos: THREE.Vector3,
  fire: { x: number; y: number; z: number }, // the PAN point (plan 25 — already lifted to the cooktop/campfire height)
  dt: number,
): void {
  let dx = fire.x - playerPos.x, dz = fire.z - playerPos.z;
  const d = Math.hypot(dx, dz) || 1e-6;
  dx /= d; dz /= d;
  if (!cookSeeded) {
    _camPos.copy(camera.position);
    _camTarget.set(playerPos.x, playerPos.y + 1.2, playerPos.z);
    cookSeeded = true;
  }
  // First person, gazing down at the pan: at eye level a hair forward of the
  // head (body behind the lens), aimed just over the skillet.
  _desiredPos.set(playerPos.x + dx * 0.2, playerPos.y + 1.7, playerPos.z + dz * 0.2);
  _desiredTarget.set(fire.x, fire.y + 0.15, fire.z);
  easeApply(camera, dt);
}

/** Ease back to the follow pose after the cook resolves / cancels (mirrors talk exit). */
export function updateCookExitCamera(
  camera: THREE.PerspectiveCamera,
  target: THREE.Object3D,
  camYaw: number,
  dt: number,
): void {
  updateTalkExitCamera(camera, target, camYaw, dt);
}

// ---- Journal "reading" camera ---------------------------------------------
// While the journal is open the camera eases to a front three-quarter close-up of the
// player (so you see them holding the book), keeping the same camYaw orientation so the
// world doesn't spin. Reuses the same eased _camPos/_camTarget as the talk camera —
// reading and talking are mutually exclusive, so they never fight over it.
let journalSeeded = false;

/** Re-seed the eased journal camera so opening the book eases from the live view. */
export function resetJournalCamera(): void {
  journalSeeded = false;
}

export function updateJournalCamera(
  camera: THREE.PerspectiveCamera,
  target: THREE.Object3D,
  camYaw: number,
  dt: number,
): void {
  if (!journalSeeded) {
    _camPos.copy(camera.position);
    _camTarget.set(target.position.x, target.position.y + 1.2, target.position.z);
    journalSeeded = true;
  }
  // Just zoom IN from the normal follow pose — same behind+above direction (no orbit to
  // the front, which read as a spin), pulled to ~45% of the follow distance and aimed a
  // touch higher at the chest so the player fills the frame.
  const off = CAM_OFFSET.clone().multiplyScalar(0.45).applyAxisAngle(UP, camYaw);
  _desiredPos.copy(target.position).add(off);
  _desiredTarget.set(target.position.x, target.position.y + 1.4, target.position.z);
  easeApply(camera, dt);
}

/** Ease back from the reading close-up to the follow pose (mirrors updateTalkExitCamera). */
export function updateJournalExitCamera(
  camera: THREE.PerspectiveCamera,
  target: THREE.Object3D,
  camYaw: number,
  dt: number,
): void {
  updateTalkExitCamera(camera, target, camYaw, dt);
}
